import { GetStaticPaths, GetStaticProps } from 'next';

import { KnowledgeArticle } from '../../../components/KnowledgeArticle/KnowledgeArticle';
import { KnowledgeCentreWithSearch } from '../../../components/KnowledgeArticle/KnowledgeCentreWithSearch';
import {
  getKnowledgeArticleByUrlName,
  getKnowledgeArticles,
} from '../../../utils/knowledge/knowledgeArticles';
import {
  isKnowledgeLocale,
  KnowledgeLocale,
  resolveKnowledgeLocale,
} from '../../../utils/knowledge/knowledgeCopy';
import { KnowledgeArticle as KnowledgeArticleType, KnowledgeChannel } from '../../../utils/knowledge/types';
import { getKnowledgeMinimalPageProps } from '../../../utils/knowledge/getKnowledgeMinimalPageProps';
import { NEXT_ISR_REVALIDATE_SECONDS } from '../../../utils/isr';
import { ServiceOSGlobalSettings } from '../../../utils/getServiceOSGlobalSettings';

const CHANNEL: KnowledgeChannel = 'help';

interface HelpArticlePageProps {
  article: KnowledgeArticleType;
  locale: KnowledgeLocale;
  topArticles: { urlName: string; title: string }[];
  serviceOS: ServiceOSGlobalSettings;
}

const HelpArticlePage = ({ article, locale, topArticles }: HelpArticlePageProps) => {
  const allArticles = getKnowledgeArticles(locale, CHANNEL);
  const description = article.summary || article.title;

  return (
    <KnowledgeCentreWithSearch
      channel={CHANNEL}
      locale={locale}
      title={`${article.title} | Urban help`}
      description={description}
      articles={allArticles}
    >
      <KnowledgeArticle
        article={article}
        locale={locale}
        channel={CHANNEL}
        topArticles={topArticles}
      />
    </KnowledgeCentreWithSearch>
  );
};

export default HelpArticlePage;

export const getStaticPaths: GetStaticPaths<{ urlName: string }> = async () => ({
  paths: [],
  fallback: 'blocking',
});

export const getStaticProps: GetStaticProps<HelpArticlePageProps> = async ({ params, locale }) => {
  const resolvedLocale = resolveKnowledgeLocale(locale);

  if (!isKnowledgeLocale(resolvedLocale) || !params?.urlName) {
    return { notFound: true };
  }

  const article = getKnowledgeArticleByUrlName(String(params.urlName), resolvedLocale, CHANNEL);

  if (!article) {
    return { notFound: true };
  }

  const allArticles = getKnowledgeArticles(resolvedLocale, CHANNEL);
  const topArticles = allArticles.slice(0, 5).map(({ urlName, title }) => ({ urlName, title }));

  const minimalProps = await getKnowledgeMinimalPageProps(resolvedLocale);

  return {
    props: {
      article,
      locale: resolvedLocale,
      topArticles,
      ...minimalProps,
    },
    revalidate: NEXT_ISR_REVALIDATE_SECONDS,
  };
};
